/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useProviderSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling provider form submission and deletion.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createAiProvider, updateAiProvider, deleteAiProvider } from '../api/aiProvidersApi';
import type { AiProviderForm } from './useProviderData';

import { sanitizeForm } from './useProviderData';

function mapFormToPayload(form: AiProviderForm) {
  return {
    domain_id: form.domainId,
    provider: form.provider,
    name: form.name,
    api_key: form.apiKey,
    base_url: form.baseUrl ? form.baseUrl : null,
    organization: form.organization ? form.organization : null,
    api_key_type: ['azure', 'google'].includes(form.provider) && form.azureApiKeyType ? form.azureApiKeyType : null,
    capabilities: form.capabilities,
    status: form.status
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiToForm(payload: any): AiProviderForm {
  return sanitizeForm({
    id: payload.id,
    domainId: payload.domain_id ?? 1,
    provider: payload.provider ?? '',
    name: payload.name ?? '',
    apiKey: payload.api_key ?? '',
    baseUrl: payload.base_url ?? '',
    organization: payload.organization ?? '',
    azureApiKeyType: payload.api_key_type ?? '',
    capabilities: Array.isArray(payload.capabilities) ? payload.capabilities : [],
    status: payload.status ?? true
  });
}

export function useProviderSubmit(
  formData: AiProviderForm,
  baseline: AiProviderForm | null,
  hasChanges: boolean,
  validateForm: () => boolean,
  mode: 'create' | 'edit',
  id: string | undefined,
  setFormData: (data: AiProviderForm | ((prev: AiProviderForm) => AiProviderForm)) => void,
  setBaseline: (data: AiProviderForm | null) => void
) {
  const { t } = useI18n();
  const { showError, showSuccess, showInfo } = useNotifications();

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Submit provider - Always-Active Save Standard: always attempt to save
  const submitProvider = useCallback(async (): Promise<AiProviderForm | undefined> => {
    const isEditMode = mode === 'edit';

    if (!validateForm()) {
      showError(t('aiProviders.messages.validationError'));
      return undefined;
    }

    setIsSaving(true);
    try {
      const payload = mapFormToPayload(formData);

      const response = isEditMode && id && id !== 'new'
        ? await updateAiProvider(id, payload)
        : await createAiProvider(payload);

      const normalized = mapApiToForm(response?.data ?? payload);
      setFormData(normalized);
      setBaseline(normalized);

      const successMessage = isEditMode
        ? t('aiProviders.messages.updateSuccess')
        : t('aiProviders.messages.createSuccess');

      showSuccess(successMessage);

      // Return the saved provider data
      return normalized;
    } catch (error) {
      const message =
        error instanceof Error && error.message && error.message !== 'REQUEST_FAILED'
          ? error.message
          : t('aiProviders.messages.saveError');
      showError(message);
      return undefined;
    } finally {
      setIsSaving(false);
    }
  }, [
    formData,
    baseline,
    hasChanges,
    id,
    mode,
    setFormData,
    setBaseline,
    showError,
    showInfo,
    showSuccess,
    t,
    validateForm
  ]);

  // Handle form submit
  const handleFormSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      submitProvider();
    },
    [submitProvider]
  );

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (baseline) {
      setFormData(baseline);
    } else {
      setFormData({
        id: undefined,
        domainId: 1,
        provider: '',
        name: '',
        apiKey: '',
        baseUrl: '',
        organization: '',
        azureApiKeyType: '',
        capabilities: [],
        status: true
      });
    }
  }, [baseline, setFormData]);

  // Handle delete
  const handleDelete = useCallback(async () => {
    if (mode !== 'edit' || !id) return;

    setIsDeleting(true);
    try {
      await deleteAiProvider(id);
      showSuccess(t('aiProviders.messages.deleteSuccess'));
      // Navigation will be handled by the form view
    } catch (error) {
      const message =
        error instanceof Error && error.message && error.message !== 'REQUEST_FAILED'
          ? error.message
          : t('aiProviders.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [id, mode, showError, showSuccess, t]);

  return {
    isSaving,
    isDeleting,
    showDeleteModal,
    setShowDeleteModal,
    handleFormSubmit,
    submitProvider,
    handleCancel,
    handleDelete
  };
}

